import type {
  QuestionnaireLogic,
  QuestionnaireLogicAction,
  QuestionnaireLogicCondition,
  QuestionnaireLogicConditionVarInner
} from "src/state/QuestionnaireCubit/QuestionnaireCubit";
import type {
  QuestionnaireEndScreen,
  QuestionnaireField
} from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";

export interface ConditionNode {
  type: "comparison" | "group";
  operator?: "AND" | "OR";
  text?: string;
  matches?: boolean;
  children?: ConditionNode[];
  left?: string;
  op?: string;
  right?: string;
}

export interface ParsedAction {
  actionType: string;
  description: string;
  conditionTree: ConditionNode;
  wouldTrigger: boolean;
  skippedByPriorJump: boolean;
  rawAction: QuestionnaireLogicAction;
}

export interface ParsedLogic {
  fieldTitle: string;
  fieldRef: string;
  actions: ParsedAction[];
}

const OP_LABELS: Record<string, string> = {
  is: "equals",
  is_not: "does not equal",
  equal: "equals",
  not_equal: "does not equal",
  greater_than: ">",
  greater_equal_than: ">=",
  lower_than: "<",
  lower_equal_than: "<=",
  contains: "contains",
  not_contains: "does not contain",
  begins_with: "begins with",
  ends_with: "ends with",
  always: "always"
};

function isVarInner(
  v: QuestionnaireLogicCondition | QuestionnaireLogicConditionVarInner
): v is QuestionnaireLogicConditionVarInner {
  return "type" in v && "value" in v && !("op" in v);
}

export type ActionEvaluator = (
  action: QuestionnaireLogicAction,
  fields: QuestionnaireField[]
) => boolean;

export type ConditionEvaluator = (
  condition: QuestionnaireLogicCondition,
  fields: QuestionnaireField[]
) => boolean;

export function parseLogic(
  logicItems: QuestionnaireLogic[],
  fields: QuestionnaireField[],
  endScreens: QuestionnaireEndScreen[],
  evaluateAction?: ActionEvaluator,
  evaluateCondition?: ConditionEvaluator
): ParsedLogic[] {
  const getFieldByRef = (ref: string): QuestionnaireField | undefined =>
    fields.find((f) => f.ref === ref || f.id === ref);

  const getEndScreenByRef = (ref: string): QuestionnaireEndScreen | undefined =>
    endScreens.find((s) => s.ref === ref || s.id === ref);

  const getChoiceLabel = (
    field: QuestionnaireField | undefined,
    choiceRef: string
  ): string => {
    if (!field) return `[choice:${choiceRef.slice(0, 8)}...]`;
    const choice = field.properties?.choices?.find(
      (c: { ref?: string; id?: string; label?: string }) =>
        c.ref === choiceRef || c.id === choiceRef
    );
    return choice?.label ?? `[choice:${choiceRef.slice(0, 8)}...]`;
  };

  const getFieldTitle = (ref: string, type?: string): string => {
    if (type === "thankyou") {
      const endScreen = getEndScreenByRef(ref);
      if (endScreen?.title) {
        const { title } = endScreen;
        return title.length > 40 ? title.slice(0, 40) + "..." : title;
      }
      return ref;
    }

    const field = getFieldByRef(ref);
    if (field?.title) {
      const { title } = field;
      return title.length > 40 ? title.slice(0, 40) + "..." : title;
    }
    return ref;
  };

  const resolveValue = (
    v: QuestionnaireLogicConditionVarInner,
    contextField?: QuestionnaireField
  ): { text: string; type: "field" | "variable" | "hidden" | "value" } => {
    const val = String(v.value);
    switch (v.type) {
      case "field":
        return { text: `@${getFieldTitle(val)}`, type: "field" };
      case "choice":
        return {
          text: `"${getChoiceLabel(contextField, val)}"`,
          type: "value"
        };
      case "variable":
        return { text: `$${val}`, type: "variable" };
      case "hidden":
        return { text: `#${val}`, type: "hidden" };
      case "constant":
        return {
          text: typeof v.value === "string" ? `"${v.value}"` : String(v.value),
          type: "value"
        };
      default:
        return { text: String(v.value), type: "value" };
    }
  };

  const parseCondition = (
    condition: QuestionnaireLogicCondition,
    contextField?: QuestionnaireField
  ): ConditionNode => {
    const { op } = condition;
    const vars = condition.vars ?? [];

    if (op === "always") {
      return {
        type: "comparison",
        text: "always",
        matches: true
      };
    }

    if (op === "and" || op === "or") {
      const children = vars.map((v) => {
        if (isVarInner(v)) {
          const resolved = resolveValue(v, contextField);
          return {
            type: "comparison" as const,
            text: resolved.text
          };
        }
        return parseCondition(v, contextField);
      });

      const childrenWithMatches = children.map((child, idx) => {
        const subCondition = vars[idx];
        if (evaluateCondition && !isVarInner(subCondition)) {
          const matches = evaluateCondition(subCondition, fields);
          return { ...child, matches };
        }
        return child;
      });

      return {
        type: "group",
        operator: op === "and" ? "AND" : "OR",
        children: childrenWithMatches
      };
    }

    const innerVars = vars.filter(isVarInner);
    if (innerVars.length >= 2) {
      const fieldVar = innerVars.find((v) => v.type === "field");
      const field = fieldVar
        ? getFieldByRef(String(fieldVar.value))
        : contextField;

      const left = resolveValue(innerVars[0], field);
      const right = resolveValue(innerVars[1], field);
      const opLabel = OP_LABELS[op] ?? op;

      let matches: boolean | undefined;
      if (evaluateCondition) {
        matches = evaluateCondition(condition, fields);
      }

      return {
        type: "comparison",
        left: left.text,
        op: opLabel,
        right: right.text,
        matches
      };
    }

    if (innerVars.length === 1) {
      const resolved = resolveValue(innerVars[0], contextField);
      return {
        type: "comparison",
        text: resolved.text
      };
    }

    return { type: "comparison", text: op };
  };

  const resolveDetailValue = (detail: {
    type: string;
    value: string | number;
  }): string => {
    return resolveValue(
      detail as QuestionnaireLogicConditionVarInner,
      undefined
    ).text;
  };

  const parseActionDescription = (action: QuestionnaireLogicAction): string => {
    const { action: actionType, details } = action;

    switch (actionType) {
      case "jump":
        if ("to" in details && details.to) {
          const targetTitle = getFieldTitle(details.to.value, details.to.type);
          const prefix =
            details.to.type === "thankyou" ? "Jump to ending: " : "Jump to ";
          return `${prefix}"${targetTitle}"`;
        }
        return "Jump";

      case "set":
        if ("target" in details && "value" in details) {
          const targetName = details.target.value;
          const value = resolveDetailValue(details.value);
          return `Set $${targetName} = ${value}`;
        }
        return "Set variable";

      case "add":
        if ("target" in details && "value" in details) {
          const value = resolveDetailValue(details.value);
          return `Add ${value} to $${details.target.value}`;
        }
        return "Add to variable";

      case "divide":
        if ("target" in details && "value" in details) {
          const value = resolveDetailValue(details.value);
          return `Divide $${details.target.value} by ${value}`;
        }
        return "Divide variable";

      case "multiply":
        if ("target" in details && "value" in details) {
          const value = resolveDetailValue(details.value);
          return `Multiply $${details.target.value} by ${value}`;
        }
        return "Multiply variable";

      default:
        return actionType;
    }
  };

  return logicItems
    .filter((item): item is QuestionnaireLogic & { ref: string } =>
      Boolean(item.ref)
    )
    .map((item) => {
      const field = getFieldByRef(item.ref);
      let jumpAlreadyFound = false;

      return {
        fieldTitle: field?.title ?? item.ref,
        fieldRef: item.ref,
        actions: item.actions.map((action) => {
          const wouldTrigger = evaluateAction
            ? evaluateAction(action, fields)
            : false;
          const isJump = action.action === "jump";

          const skippedByPriorJump = isJump && jumpAlreadyFound;

          if (isJump && wouldTrigger && !jumpAlreadyFound) {
            jumpAlreadyFound = true;
          }

          const conditionTree = parseCondition(action.condition, field);

          return {
            actionType: action.action,
            description: parseActionDescription(action),
            conditionTree,
            wouldTrigger: skippedByPriorJump ? false : wouldTrigger,
            skippedByPriorJump,
            rawAction: action
          };
        })
      };
    });
}
